import React, { useRef, useState } from 'react'

import { useModal } from '_hooks'

import Collapsible from '../collapsible/index.jsx'
import { useEventFeedsContext } from '../../context/index.jsx'
import CustomAttributesList from './custom-attributes-list/index.jsx'
import AddCustomAttribute from './add-custom-attribute/index.jsx'
import CustomAttributesModal from './modal-add-custom-attributes/index.jsx'

import { updateEventFeed } from '_services/data-explorer'
import { logErrorAsync } from '../../../../lib/utility.js'
import ManageComponent, { useIsSnapshot } from '../../manage-component/index.jsx'

const CustomAttributeConfiguration = ({}, ref) => {
  const {
    id,
    data,
    querySectionsCollapsed: { customAttribute: customAttributeCollapsed },
    dispatch,
    ACTIONS,
  } = useEventFeedsContext()
  const modal = useModal(false)
  const isSnapshot = useIsSnapshot()

  const popupRef = useRef()

  const [current, setCurrent] = useState()

  const handleSaveChanges = async (newData) => {
    const response = await updateEventFeed(id, newData.data)
    try {
      dispatch({
        type: ACTIONS.SET_DATA,
        payload: response,
      })
      toast('Changes saved')
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    }
  }

  const handleDelete = (alias) => {
    dispatch(
      {
        type: ACTIONS.REMOVE_CUSTOM_ATTRIBUTE,
        payload: {
          alias,
        },
      },
      (newData) => handleSaveChanges(newData)
    )
  }

  const handleUpsert = (customAttribute, oldValue) => {
    if (!customAttribute?.alias) {
      toast.error('You must provide a name.')
      return
    }
    if (!oldValue || Object.keys(oldValue).length === 0) {
      if (data.customAttributes.some((attr) => attr.alias === customAttribute.alias)) {
        toast.error('This name is already being used.')
        return
      }
      dispatch(
        {
          type: ACTIONS.ADD_CUSTOM_ATTRIBUTE,
          payload: {
            customAttribute,
          },
        },
        (newData) => handleSaveChanges(newData)
      )
    } else {
      dispatch(
        {
          type: ACTIONS.EDIT_CUSTOM_ATTRIBUTE,
          payload: {
            alias: oldValue.alias,
            customAttribute,
          },
        },
        (newData) => handleSaveChanges(newData)
      )
    }
    popupRef.current.close()
  }

  const handleDeleteAll = async () => {
    try {
      const newData = { ...data, customAttributes: [] }
      dispatch({ type: ACTIONS.SET_DATA, payload: newData })
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    }
  }

  const setCustomAttributeCollapsed = (value) => {
    dispatch({ type: ACTIONS.UPDATE_QUERY_SECTION_COLLAPSED, payload: { customAttribute: !value } })
  }

  return (
    <Collapsible
      ref={ref}
      title="Custom Attrib."
      onOpenClick={modal.handleOpenModal}
      showOpenButton={!isSnapshot}
      onDeleteAllClick={handleDeleteAll}
      showDeleteAllButton={!isSnapshot && data.customAttributes?.length > 0}
      managedState={[!customAttributeCollapsed, setCustomAttributeCollapsed]}
    >
      <CustomAttributesModal id={id} isOpen={modal.isOpen} onClose={modal.handleCloseModal} />
      <CustomAttributesList
        list={data.customAttributes}
        onDelete={(id, value) => handleDelete(value.alias)}
        onEdit={(id, value) => {
          setCurrent(value)
          popupRef.current.open()
        }}
      />
      <ManageComponent>
        <AddCustomAttribute
          ref={popupRef}
          value={current}
          onUpsert={handleUpsert}
          onClear={() => setCurrent({})}
        />
      </ManageComponent>
    </Collapsible>
  )
}

export default React.forwardRef(CustomAttributeConfiguration)
